import { z } from 'zod/v4';
import { ETravelTypes } from './travel-types.enum';
import { ViaName } from './relation.interfaces';

export interface ITariffServiceArea {
  id: number;
  name: string;
  nameLang: string | null;
  version?: string | null;
}

export interface IRelationProductGroupQuery {
  serviceAreaId: number;
  origin: string;
  destination: string;
  dateTime: string;
  via?: string;
}

export interface ISystemPaymentQuery {
  tariffAreaId: number;
  externalReference: string;
  dateTime?: string;
}

export interface ITariffSystemPayment {
  name: string;
  externalReference: string;
  price: {
    priceIncludingVat: number;
    vatRate: number;
    taxPrice: number;
    netPrice: number;
  };
}

export const TARIFF_SERVICE_AREA: z.ZodType<ITariffServiceArea> = z.object({
  id: z.number(),
  name: z.string(),
  nameLang: z.string().nullable().default(null),
  version: z.string().nullable().default(null),
});

export const TARIFF_ADDITIONAL_ENTRY: z.ZodType<ITariffAdditionalEntry> =
  z.object({
    name: z.string().nullable().default(null),
    externalReference: z.string().nullable().default(null),
    dataType: z.string(),
    additionalEntryDataType: z.string(),
    printField: z.string(),
    storageLocation: z.string(),
    kaUsage: z.string(),
    options: z.array(z.string()).nullable().default(null),
    printRelevant: z.boolean(),
    registrationRelevant: z.boolean(),
    kaRelevant: z.boolean(),
    minValue: z.number().nullable().default(null),
    maxValue: z.number().nullable().default(null),
    optional: z.boolean(),
    maxLength: z.number().int().nullable().default(null),
    minLength: z.number().int().nullable().default(null),
    sortOrder: z.number().int(),
  });

export const TARIFF_SORT: z.ZodType<ITariffSort> = z.object({
  id: z.number(),
  variablePrice: z.boolean(),
  serviceArea: TARIFF_SERVICE_AREA.optional(),
  externalReference: z.string(),
  sortType: z.string(),
  paymentType: z.string(),
  name: z.string(),
  additionalEntries: z.array(TARIFF_ADDITIONAL_ENTRY).default([]),
});

export const TARIFF_SORTS: z.ZodType<ITariffSort[]> = z.array(TARIFF_SORT);

export interface ITariffAdditionalEntry {
  name: string | null;
  externalReference: string | null;
  dataType: string;
  additionalEntryDataType: string;
  printField: string;
  storageLocation: string;
  kaUsage: string;
  options: string[] | null;
  printRelevant: boolean;
  registrationRelevant: boolean;
  kaRelevant: boolean;
  minValue: number | null;
  maxValue: number | null;
  optional: boolean;
  maxLength: number | null;
  minLength: number | null;
  sortOrder: number;
}

export interface ITariffSort {
  uuid?: string;
  id: number;
  variablePrice: boolean;
  serviceArea?: ITariffServiceArea;
  externalReference: string;
  sortType: string;
  paymentType: string;
  name: string;
  additionalEntries: ITariffAdditionalEntry[];
}

export type AdditionalEntryValueType = string | number | Date | null;

export interface IAdditionalEntryValue {
  externalReference: string | null;
  name: string | null;
  value: AdditionalEntryValueType;
}

export const TARIFF_SELECTION_ATTRIBUTE: z.ZodType<TariffSelectionAttribute> =
  z.object({
    name: z.string(),
    values: z.array(z.string().nullable().default(null)),
  });

export type TariffSelectionAttributeValue = string | null;

export interface TariffSelectionAttribute {
  name: string;
  values: TariffSelectionAttributeValue[];
}

export const PRODUCT_DEFINITION_PRICE_LEVEL: z.ZodType<ProductDefinitionPriceLevel> =
  z.object({
    serviceArea: TARIFF_SERVICE_AREA,
    externalReference: z.string(),
    name: z.string(),
  });

export interface ProductDefinitionPriceLevel {
  serviceArea: ITariffServiceArea;
  externalReference: string;
  name: string;
}

export const PRICE_LEVEL_VALIDITY_OPTION: z.ZodType<PriceLevelValidityOption> =
  z.object({
    type: z.string(),
    dateList: z.array(z.string()),
    earliestValidStart: z.string(),
    latestValidStart: z.string(),
    default: z.string(),
  });

export interface PriceLevelValidityOption {
  type: string;
  dateList: string[];
  earliestValidStart: string;
  latestValidStart: string;
  default: string;
}

export const TRAVEL_TYPES_WITH_TIME_PICKER: string[] = [
  ETravelTypes.ListeZeit,
  ETravelTypes.DatumZeit,
];

export interface NamedTariffProductDefinition extends TariffProductDefinition {
  name: string;
}

export const TARIFF_PRODUCT_DEFINITION: z.ZodType<TariffProductDefinition> =
  z.object({
    keyElements: z.array(
      z.object({
        name: z.string(),
        value: z.string().nullable().default(null),
      })
    ),
    priceLevel: PRODUCT_DEFINITION_PRICE_LEVEL,
    riderType: z.string(),
    discountClass: z.string(),
    comfortClass: z.string(),
    ticketInvalidations: z.string(),
    numberOfPeople: z.number().nullable().default(null),
    numberOfDrives: z.number().nullable().default(null),
    via: z
      .string()
      .transform(s => s as ViaName)
      .nullable()
      .default(null),
    transferable: z.boolean().nullable().default(null),
    validityOptions: PRICE_LEVEL_VALIDITY_OPTION,
    minNumberOfPeople: z.number().nullable().default(null),
    maxNumberOfPeople: z.number().nullable().default(null),
    standardNumberOfPeople: z.number().nullable().default(null),
    canBeCancelled: z.boolean(),
    additionalValues: z.unknown(),
    layout: z.string().nullable().default(null),
    numberOfSections: z.number().nullable().default(null),
    serviceArea: TARIFF_SERVICE_AREA,
    versionNumber: z.string(),
    sort: TARIFF_SORT,
    externalReference: z.string().nullable().default(null),
    price: z.object({
      priceIncludingVat: z.number(),
      vatRate: z.number(),
      taxPrice: z.number(),
      netPrice: z.number(),
    }),
  });

export const TARIFF_SYSTEM_PAYMENT: z.ZodType<ITariffSystemPayment> = z.object({
  name: z.string(),
  externalReference: z.string(),
  price: z.object({
    priceIncludingVat: z.number(),
    vatRate: z.number(),
    taxPrice: z.number(),
    netPrice: z.number(),
  }),
});

export const TARIFF_SYSTEM_PAYMENTS: z.ZodType<ITariffSystemPayment[]> =
  z.array(TARIFF_SYSTEM_PAYMENT);

export interface TariffProductDefinition {
  keyElements: {
    name: string;
    value: TariffSelectionAttributeValue;
  }[];
  priceLevel: ProductDefinitionPriceLevel;
  riderType: string;
  discountClass: string;
  comfortClass: string;
  ticketInvalidations: string;
  numberOfPeople: number | null;
  numberOfDrives: number | null;
  via: ViaName | null;
  transferable: boolean | null;
  validityOptions: PriceLevelValidityOption;
  minNumberOfPeople: number | null;
  maxNumberOfPeople: number | null;
  standardNumberOfPeople: number | null;
  canBeCancelled: boolean;
  additionalValues: unknown;
  layout: string | null;
  numberOfSections: number | null;
  serviceArea: ITariffServiceArea;
  versionNumber: string;
  sort: ITariffSort;
  externalReference: string | null;
  price: {
    priceIncludingVat: number;
    vatRate: number;
    taxPrice: number;
    netPrice: number;
  };
}

export interface TariffProductGroup {
  name: string;
  selectionAttributes: TariffSelectionAttribute[];
  productDefinitions: TariffProductDefinition[];
}

export function productGroupsToString(
  productGroups: TariffProductGroup[]
): string {
  const uniqueAttributes = productGroups.map(pg => ({
    name: pg.name,
    productGroupDefinitions: pg.productDefinitions.map(def => ({
      via: def.via,
      serviceArea: def.serviceArea.id,
      sort: {
        id: def.sort.id,
        externalReference: def.externalReference,
        serviceArea: def.serviceArea.id,
        sortTyp: def.sort.sortType,
      },
      externalReference: def.externalReference,
    })),
    selectionAttributes: pg.selectionAttributes,
  }));

  return JSON.stringify(uniqueAttributes);
}
